import { DownloadOutlined, QuestionCircleOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Row,
  Skeleton,
  Typography,
  notification
} from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { CalculationHelp } from "./CalculationHelp"
import { PerformanceFilters } from "./PerformanceFilters"
import { PerformanceKpiCards } from "./PerformanceKpiCards"
import {
  type MemberDrill,
  PerformanceMembersTable
} from "./PerformanceMembersTable"
import { fetchTeamPerformance } from "./api"
import { downloadPerformanceCsv } from "./member/api"
import type { PerformanceMemberRow, TeamPerformanceResponse } from "./types"
import { usePerformanceUrlState } from "./use-performance-url-state"

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: TeamPerformanceResponse }

function LoadingState() {
  return (
    <Flex
      vertical
      gap="middle"
      data-testid="performance-loading"
      aria-busy="true"
    >
      <Row gutter={[12, 12]}>
        {Array.from({ length: 6 }, (_, i) => (
          <Col key={i} xs={12} md={8} xl={4}>
            <Card size="small">
              <Skeleton active title={false} paragraph={{ rows: 2 }} />
            </Card>
          </Col>
        ))}
      </Row>
      <Card size="small">
        <Skeleton active title={false} paragraph={{ rows: 6 }} />
      </Card>
    </Flex>
  )
}

function DataQualityNotice({ data }: { data: TeamPerformanceResponse }) {
  const { t } = useTranslation()
  const dq = data.data_quality
  const parts = [
    dq.missing_finance &&
      t("teamPerformance.quality.missingFinance", {
        count: dq.missing_finance
      }),
    dq.missing_planned_end &&
      t("teamPerformance.quality.missingPlannedEnd", {
        count: dq.missing_planned_end
      }),
    dq.ambiguous_assignment &&
      t("teamPerformance.quality.ambiguous", { count: dq.ambiguous_assignment })
  ].filter(Boolean)
  const excluded = [
    dq.missing_close_date &&
      t("teamPerformance.quality.missingCloseDate", {
        count: dq.missing_close_date
      }),
    dq.unassigned &&
      t("teamPerformance.quality.unassigned", { count: dq.unassigned })
  ].filter(Boolean)
  const fallback = dq.actual_end_from_close_date
    ? t("teamPerformance.quality.actualFromClose", {
        count: dq.actual_end_from_close_date
      })
    : null
  if (!parts.length && !excluded.length && !fallback) return null
  return (
    <Alert
      type="info"
      showIcon
      message={t("teamPerformance.quality.title")}
      description={[
        parts.length ? `${parts.join(" · ")}.` : null,
        ...excluded,
        fallback
      ]
        .filter(Boolean)
        .join(" ")}
    />
  )
}

// The whole tab reads the server's aggregates as they are: the client never
// rebuilds a score from rows (spec §5 "Query and performance expectations").
export function PerformanceDashboard() {
  const { t } = useTranslation()
  const { query, sort, update } = usePerformanceUrlState()
  const [, setSearchParams] = useSearchParams()
  const [state, setState] = useState<LoadState>({ status: "loading" })
  const [attempt, setAttempt] = useState(0)
  const periodRef = useRef<{ focus: () => void }>(null)
  const navigate = useNavigate()
  const [helpOpen, setHelpOpen] = useState(false)
  const queryKey = JSON.stringify(query)

  useEffect(() => {
    let current = true
    setState({ status: "loading" })
    fetchTeamPerformance(query)
      .then((data) => current && setState({ status: "ready", data }))
      .catch(() => current && setState({ status: "error" }))
    return () => {
      current = false
    }
  }, [queryKey, attempt])

  const data = state.status === "ready" ? state.data : undefined

  const openMember = (row: PerformanceMemberRow, drill?: MemberDrill) => {
    const period = data?.filters.period
    const params = new URLSearchParams({
      grain: query.grain,
      ...(period ? { from: period, to: period } : {})
    })
    // The member page narrows by the same type, and keeps the role so Back
    // returns to the dashboard the user drilled from (spec §3).
    if (query.projectType) params.set("type", query.projectType)
    if (query.role !== "all") params.set("role", query.role)
    if (drill) {
      params.set("view", "projects")
      params.set("flag", drill)
    }
    navigate(
      `${RoutesPaths.team_performance_member(row.id)}?${params.toString()}`
    )
  }

  const exportTeam = () => {
    if (!data) return
    downloadPerformanceCsv(
      "/dashboard/team-performance/",
      {
        grain: query.grain,
        period: data.filters.period,
        role: query.role,
        ...(query.projectType ? { project_type: query.projectType } : {})
      },
      `team-performance-${data.filters.period}.csv`
    ).catch(() =>
      notification.error({ message: t("teamPerformance.exportFailed") })
    )
  }

  const body = (() => {
    if (state.status === "loading") return <LoadingState />
    if (state.status === "error") {
      return (
        <Alert
          type="error"
          showIcon
          message={t("teamPerformance.states.loadFailed")}
          action={
            <Button onClick={() => setAttempt((n) => n + 1)}>
              {t("teamPerformance.states.retry")}
            </Button>
          }
        />
      )
    }
    const ready = state.data
    if (ready.summary.eligible_members === 0) {
      return (
        <Empty description={t("teamPerformance.states.noMembers")}>
          <Button onClick={() => setSearchParams({})}>
            {t("teamPerformance.states.goToOurTeam")}
          </Button>
        </Empty>
      )
    }
    if (ready.summary.projects === 0) {
      return (
        <Empty
          description={
            <Flex vertical gap={4}>
              <Typography.Text strong>
                {t("teamPerformance.states.emptyTitle")}
              </Typography.Text>
              <Typography.Text type="secondary">
                {t("teamPerformance.states.emptyBody", {
                  period: ready.filters.period_label
                })}
              </Typography.Text>
            </Flex>
          }
        >
          <Flex gap="small" justify="center">
            <Button onClick={() => periodRef.current?.focus()}>
              {t("teamPerformance.states.changePeriod")}
            </Button>
            <Button onClick={() => update({ role: "all", projectType: null })}>
              {t("teamPerformance.states.clearFilters")}
            </Button>
          </Flex>
        </Empty>
      )
    }
    return (
      <Flex vertical gap="middle">
        <DataQualityNotice data={ready} />
        <PerformanceKpiCards data={ready} />
        <PerformanceMembersTable
          rows={ready.members}
          amountsVisible={ready.amounts_visible}
          sort={sort}
          onSortChange={(next) => update({ sort: next })}
          onOpenMember={openMember}
        />
      </Flex>
    )
  })()

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="flex-end" wrap gap="small">
        <PerformanceFilters
          query={query}
          resolvedPeriod={data?.filters.period}
          projectTypes={data?.project_types ?? []}
          onChange={update}
          periodRef={periodRef}
        />
        <Flex gap="small">
          <Button
            icon={<QuestionCircleOutlined />}
            onClick={() => setHelpOpen(true)}
          >
            {t("teamPerformance.help.button")}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={exportTeam}
            disabled={!data}
          >
            {t("teamPerformance.export")}
          </Button>
        </Flex>
      </Flex>
      <CalculationHelp
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        thresholds={data?.thresholds}
        versions={data?.score_versions}
      />
      <div role="status" aria-live="polite" className="sr-only">
        {data
          ? t("teamPerformance.states.loaded", { count: data.members.length })
          : ""}
      </div>
      {body}
    </Flex>
  )
}
